import { useEffect, useRef } from 'react';

import { FlameStage } from 'components/Streaks/model/milestones';

/**
 * Flames rising from the bottom of the felt (EN-11163 beat two), as the
 * celebration reference draws them (StreakCelebration, embers + glow): a
 * fixed handful of small flames, 18 for a day, 12 for a first day and 28 for
 * a milestone on a phone felt, each starting in a band near the bottom, rising
 * once along one of four drifting paths while it grows then shrinks and fades,
 * over a warm glow that swells from the bottom edge. The count scales with
 * the felt width up to the reference's 2.4x for the desktop grid (JRam 11/03:
 * the old particle stream drew hundreds of flames).
 *
 * Transient by design: everything is timed to end within `durationMs`, then
 * the frame loop stops, so nothing runs once the moment is over. Each flame
 * rises once, stretched over the whole burst, so the flames come in once and
 * last until the count goes (JRam 11/03: a capped rise came in twice).
 *
 * PERF: 30 fps, device pixel ratio capped at 1.5, at most ~67 flames.
 */
export type RisingFlamesKind = 'daily' | 'first' | 'milestone';

export const BASE_COUNT: Record<RisingFlamesKind, number> = { daily: 18, first: 12, milestone: 28 };

/** The reference count for a felt this wide: the phone count, up to 2.4x on wide felts. */
export function flameCountFor(kind: RisingFlamesKind, width: number): number {
  return Math.round(BASE_COUNT[kind] * Math.min(2.4, Math.max(1, width / 800)));
}

/**
 * Demo-only knobs (the /demo-table "Flames" controls), so a designer can try
 * other looks live; production never passes them. Each is a multiplier on the
 * reference look except `count`, which replaces the flame count outright.
 */
export type FlameTuning = {
  /** Flames on this felt, in place of the reference count for its width. */
  count?: number;
  /** Movement speed: 2 travels twice as far in the same time; when flames fade in, peak and fade out stays put. */
  speed?: number;
  /** Sideways drift: 0 rises straight up, 2 drifts twice as far. */
  drift?: number;
  /** Flame size. */
  size?: number;
};

/** The reference's flame, in its 48-unit box. */
const FLAME_PATH =
  'M24 3C26.2 10.5 35.5 15.5 35.5 27.5C35.5 37.2 30.4 44.5 24 44.5C17.6 44.5 12.5 37.6 12.5 29.2C12.5 23.2 15.4 19 18.4 16C18.2 20.6 19.8 23.6 22.2 25C21 17.6 21.6 9.8 24 3Z';

/** Each stage's flame fill (centre, middle, edge), stages 1 to 6. */
const STAGE_FILLS: [string, string, string][] = [
  ['#ffd9a0', '#e8621f', '#8f2410'],
  ['#fff0c9', '#f39a34', '#b4410f'],
  ['#fffbe6', '#ffc43f', '#b5761a'],
  ['#ffffff', '#ffe39a', '#d19a2a'],
  ['#ffffff', '#d6e9ff', '#6fa0e8'],
  ['#e9f7ff', '#5f9bff', '#2a3fb8']
];

const STAGE_GLOWS = [
  'rgba(232,90,30,0.42)',
  'rgba(240,130,40,0.45)',
  'rgba(255,180,60,0.45)',
  'rgba(255,220,140,0.45)',
  'rgba(150,200,255,0.42)',
  'rgba(80,140,255,0.48)'
];

/** The four rise paths: offset at the peak (scale 1), then at the end, on the 844 px phone felt. */
const PATHS: [number, number, number, number][] = [
  [-14, -220, -26, -430],
  [16, -190, 30, -380],
  [-6, -250, -12, -480],
  [12, -170, 22, -340]
];
const REFERENCE_HEIGHT = 844;
/** Where on the rise (0 to 1) a flame peaks, and where it is fully in. */
const PEAK_AT = 20 / 34;
const IN_AT = 4 / 34;
/** The rise and the start stagger, as shares of the burst (the reference's 2.04 s and 0.7 s of a 2.4 s day, fitted inside it). */
const RISE_SHARE = 0.75;
const STAGGER_SHARE = 0.25;

type Ember = {
  x: number;
  y: number;
  w: number;
  delay: number;
  path: [number, number, number, number];
};

/** Deterministic noise, so a felt's flames are the same each play (the reference's rnd). */
const rnd = (i: number, s: number) => {
  const x = Math.sin(i * 12.9898 + s * 78.233) * 43758.5453;
  return x - Math.floor(x);
};
/** A rise path with its sideways part scaled by `drift` and all of it by `speed`. */
const movePath = (
  [px, py, ex, ey]: [number, number, number, number],
  drift: number,
  speed: number
): [number, number, number, number] => [
  px * drift * speed,
  py * speed,
  ex * drift * speed,
  ey * speed
];
const easeOut = (t: number) => 1 - (1 - t) * (1 - t);
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

export default function RisingFlames({
  durationMs,
  kind = 'daily',
  stage = 1,
  tuning
}: {
  durationMs: number;
  kind?: RisingFlamesKind;
  tuning?: FlameTuning;
  /** The streak's flame stage (1 to 6): sets the flame and glow colours. */
  stage?: FlameStage;
}) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return undefined;
    const dpr = Math.min(1.5, window.devicePixelRatio || 1);
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);

    const s = Math.min(6, Math.max(1, stage)) - 1;
    const flame = new Path2D(FLAME_PATH);
    const fillOf = (colors: [string, string, string]) => {
      // The reference's radialGradient cx 50% cy 80% r 72%, in the 48 box.
      const g = ctx.createRadialGradient(24, 38.4, 0, 24, 38.4, 34.6);
      g.addColorStop(0, colors[0]);
      g.addColorStop(0.5, colors[1]);
      g.addColorStop(1, colors[2]);
      return g;
    };
    const stageFill = fillOf(STAGE_FILLS[s]);

    const speed = tuning?.speed && tuning.speed > 0 ? tuning.speed : 1;
    const drift = tuning?.drift ?? 1;
    const sizeScale = tuning?.size && tuning.size > 0 ? tuning.size : 1;
    const rise = durationMs * RISE_SHARE;
    const stagger = Math.min(durationMs - rise, durationMs * STAGGER_SHARE);
    const lift = h / REFERENCE_HEIGHT;
    const inset = Math.min(36, w * 0.04);
    const count =
      tuning?.count !== undefined ? Math.max(0, Math.round(tuning.count)) : flameCountFor(kind, w);
    const embers: Ember[] = Array.from({ length: count }, (_, i) => ({
      x: inset + rnd(i, 2) * (w - 2 * inset),
      y: h * (690 / REFERENCE_HEIGHT) + rnd(i, 3) * h * (90 / REFERENCE_HEIGHT),
      w: (9 + Math.round(rnd(i, 1) * (kind === 'milestone' ? 12 : 9))) * sizeScale,
      delay: rnd(i, 4) * stagger,
      path: movePath(PATHS[i % 4], drift, speed)
    }));

    const drawGlow = (elapsed: number) => {
      // Swells in over the first fifth, eases to 0.7, gone by the end (scGlow).
      const t = elapsed / durationMs;
      const a =
        t < 0.2
          ? t / 0.2
          : t < 0.75
          ? lerp(1, 0.7, (t - 0.2) / 0.55)
          : lerp(0.7, 0, (t - 0.75) / 0.25);
      if (a <= 0) return;
      const gw = w + 80;
      const gh = Math.min(520, h * 0.62);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.globalAlpha = 0.8 * a;
      ctx.translate(w / 2, h + 60);
      ctx.scale(gw / 2, gh);
      const g = ctx.createRadialGradient(0, 0, 0, 0, 0, 0.68);
      g.addColorStop(0, STAGE_GLOWS[s]);
      g.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = g;
      ctx.fillRect(-1, -1, 2, 1);
    };

    const drawEmber = (e: Ember, t: number) => {
      const [px, py, ex, ey] = e.path;
      let dx: number;
      let dy: number;
      let scale: number;
      let alpha: number;
      if (t < PEAK_AT) {
        const k = easeOut(t / PEAK_AT);
        dx = px * k;
        dy = py * k;
        scale = lerp(0.6, 1, k);
        alpha = t < IN_AT ? t / IN_AT : lerp(1, 0.95, (t - IN_AT) / (PEAK_AT - IN_AT));
      } else {
        const k = easeOut((t - PEAK_AT) / (1 - PEAK_AT));
        dx = lerp(px, ex, k);
        dy = lerp(py, ey, k);
        scale = lerp(1, 0.45, k);
        alpha = lerp(0.95, 0, k);
      }
      if (alpha <= 0) return;
      const size = (e.w / 48) * scale;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.globalAlpha = alpha;
      // The box is w by 1.3w; the flame sits centred in it.
      ctx.translate(e.x + e.w / 2 + dx, e.y + (e.w * 1.3) / 2 + dy * lift);
      ctx.scale(size, size);
      ctx.translate(-24, -24);
      // Every flame in the stage's colour (JRam 11/03: no orange in a blue burst).
      ctx.fillStyle = stageFill;
      ctx.fill(flame);
    };

    const start = performance.now();
    let last = -Infinity;
    let raf = 0;
    const frame = (ts: number) => {
      const elapsed = ts - start;
      if (elapsed >= durationMs) {
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        return; // burned out: stop the loop
      }
      if (ts - last >= 1000 / 30) {
        last = ts;
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.globalAlpha = 1;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        drawGlow(elapsed);
        ctx.shadowColor = 'rgba(255,150,40,0.7)';
        ctx.shadowBlur = 4 * dpr;
        for (const e of embers) {
          const local = elapsed - e.delay;
          if (local < 0 || local > rise) continue;
          drawEmber(e, local / rise);
        }
        ctx.shadowBlur = 0;
      }
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [durationMs, kind, stage, tuning?.count, tuning?.speed, tuning?.drift, tuning?.size]);

  return (
    <canvas
      ref={ref}
      aria-hidden='true'
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none'
      }}
    />
  );
}
